/**
 * Enhanced error parser that builds on existing parseApiError
 * Adds error categorization, network detection, and retry logic
 */

import { type ParsedApiError, parseApiError } from "../api/error-parser";
import { getErrorMessage } from "./error-messages";
import { type AppError, ErrorCategory } from "./types";

/**
 * Determine if an error is a network error
 */
function isNetworkError(error: unknown): boolean {
	if (error instanceof TypeError && error.message.includes("fetch")) {
		return true;
	}

	if (error && typeof error === "object") {
		const errorObj = error as { name?: string; message?: string };
		return (
			errorObj.name === "NetworkError" ||
			Boolean(errorObj.message?.toLowerCase().includes("network")) ||
			Boolean(errorObj.message?.toLowerCase().includes("timeout")) ||
			Boolean(errorObj.message?.toLowerCase().includes("connection"))
		);
	}

	return false;
}

/**
 * Categorize error based on status code or error type
 */
function categorizeError(status?: number, error?: unknown): ErrorCategory {
	// Network errors take precedence
	if (isNetworkError(error)) {
		return ErrorCategory.NETWORK;
	}

	if (!status) {
		return ErrorCategory.UNKNOWN;
	}

	// Categorize by HTTP status
	if (status === 401 || status === 403) {
		return ErrorCategory.AUTH;
	}

	if (status === 404) {
		return ErrorCategory.NOT_FOUND;
	}

	if (status === 422) {
		return ErrorCategory.VALIDATION;
	}

	if (status === 400 || status === 409) {
		return ErrorCategory.BUSINESS;
	}

	if (status >= 500) {
		return ErrorCategory.SERVER;
	}

	return ErrorCategory.UNKNOWN;
}

/**
 * Determine if error should be retried automatically
 */
function isRetryable(category: ErrorCategory, status?: number): boolean {
	// Retry network errors and server errors
	if (category === ErrorCategory.NETWORK || category === ErrorCategory.SERVER) {
		return true;
	}

	// Retry rate limiting
	if (status === 429) {
		return true;
	}

	// Don't retry client errors (4xx except 429)
	return false;
}

/**
 * Enhanced error parser that creates AppError from any error
 */
export function enhancedParseError(error: unknown, response?: { status: number }): AppError {
	// Parse using existing parser
	const parsed: ParsedApiError = parseApiError(error, response);

	// Categorize the error
	const category = categorizeError(response?.status, error);

	// Determine if retryable
	const retryable: boolean = isRetryable(category, response?.status);

	// Get appropriate message (use parsed message if available, otherwise use category default)
	let message = parsed.message;

	// Override with better messages for common cases
	if (!message || message === "An unexpected error occurred. Please try again.") {
		message = getErrorMessage(response?.status, category);
	}

	return {
		message,
		category,
		status: response?.status,
		code: parsed.code,
		fieldErrors: parsed.fieldErrors,
		retryable,
		originalError: error,
	};
}

/**
 * Parse error specifically for TanStack Query
 * Extracts response from openapi-fetch error format
 */
export function parseQueryError(error: unknown): AppError {
	// openapi-fetch errors have { error, response } structure
	if (error && typeof error === "object") {
		const fetchError = error as { error?: unknown; response?: { status: number } };

		if (fetchError.response || fetchError.error) {
			return enhancedParseError(fetchError.error || error, fetchError.response);
		}
	}

	// Generic error
	return enhancedParseError(error);
}
